import React, { useState } from "react";
import { Pencil, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import backendApi from "@/api/backendApi";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

export default function ProjectsSection({ profile }) {
  const queryClient = useQueryClient();
  const projects = profile.projects || [];

  const [isOpen, setIsOpen] = useState(false);
  const [editId, setEditId] = useState(null);

  const [formData, setFormData] = useState({
    title: "",
    client: "",
    status: "Completed",
    start_date: "",
    end_date: "",
    description: "",
    url: "",
  });

  // MUTATIONS
  const saveMutation = useMutation({
    mutationFn: (data) => {
      if (editId) {
        return backendApi.entities.UserProfile.updateProject(editId, data);
      }
      return backendApi.entities.UserProfile.addProject(data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["userProfile"]);
      setIsOpen(false);
      resetForm();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => backendApi.entities.UserProfile.deleteProject(id),
    onSuccess: () => {
      queryClient.invalidateQueries(["userProfile"]);
    },
  });

  const resetForm = () => {
    setEditId(null);
    setFormData({
      title: "",
      client: "",
      status: "Completed",
      start_date: "",
      end_date: "",
      description: "",
      url: "",
    });
  };

  const openAdd = () => {
    resetForm();
    setIsOpen(true);
  };

  const openEdit = (project) => {
    setEditId(project.id);
    setFormData(project);
    setIsOpen(true);
  };

  const handleSave = () => {
    saveMutation.mutate(formData);
  };

  const handleDelete = (id) => {
    if (window.confirm("Are you sure you want to delete this project?")) {
      deleteMutation.mutate(id);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm p-6">
      {/* HEADER */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <h3 className="text-lg font-semibold text-gray-800">Projects</h3>
        </div>

        <button
          onClick={openAdd}
          className="text-blue-600 hover:underline text-sm font-medium"
        >
          {(profile?.projects && profile.projects.length > 0) ? "Edit" : "Add"}
        </button>
      </div>

      {/* PROJECT LIST */}
      {projects.length > 0 ? (
        <div className="space-y-4">
          {projects.map((project) => (
            <div
              key={project.id}
              className="border-l-2 border-blue-500 pl-4 pb-2 relative group"
            >
              <h4 className="font-medium text-gray-800 flex items-center gap-2">
                {project.title}
                <button
                  onClick={() => openEdit(project)}
                  className="text-gray-400 hover:text-blue-600"
                >
                  <Pencil className="w-3 h-3" />
                </button>
                <button
                  onClick={() => handleDelete(project.id)}
                  className="text-gray-400 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              </h4>

              {project.client && (
                <p className="text-sm text-gray-600">{project.client}</p>
              )}

              <p className="text-sm text-gray-500">
                {project.start_date || "--"} - {project.end_date || "--"} |{" "}
                {project.status}
              </p>

              {project.description && (
                <p className="text-sm text-gray-500 mt-1">
                  {project.description}
                </p>
              )}

              {project.url && (
                <a
                  href={project.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-blue-600 hover:underline mt-1 inline-block"
                >
                  View Project 🔗
                </a>
              )}
            </div>
          ))}
        </div>
      ) : (
        <p className="text-gray-500">
          Add details about projects you worked on to increase profile strength.
        </p>
      )}

      {/* ADD / EDIT PROJECT DIALOG */}
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editId ? "Edit Project" : "Add Project"}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {/* Project Title */}
            <div>
              <Label>Project Title</Label>
              <Input
                value={formData.title}
                onChange={(e) =>
                  setFormData({ ...formData, title: e.target.value })
                }
                placeholder="e.g. E-commerce Website"
              />
            </div>

            {/* Client */}
            <div>
              <Label>Client (optional)</Label>
              <Input
                value={formData.client}
                onChange={(e) =>
                  setFormData({ ...formData, client: e.target.value })
                }
                placeholder="e.g. ABC Corp"
              />
            </div>

            {/* Dates */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Start Date</Label>
                <Input
                  type="month"
                  value={formData.start_date}
                  onChange={(e) =>
                    setFormData({ ...formData, start_date: e.target.value })
                  }
                />
              </div>

              <div>
                <Label>End Date</Label>
                <Input
                  type="month"
                  value={formData.end_date}
                  onChange={(e) =>
                    setFormData({ ...formData, end_date: e.target.value })
                  }
                />
              </div>
            </div>

            {/* Description */}
            <div>
              <Label>Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                placeholder="Describe your project..."
              />
            </div>

            {/* URL Link */}
            <div>
              <Label>Project URL / GitHub Link (optional)</Label>
              <Input
                value={formData.url}
                onChange={(e) =>
                  setFormData({ ...formData, url: e.target.value })
                }
                placeholder="https://github.com/..."
              />
            </div>

            {/* Buttons */}
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setIsOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleSave}>Save</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
